import Modal from "Common/Components/Modal";
import React from "react";
import { Loader2 } from "lucide-react";
import { useAppContext } from 'context/AppContext';
import { useItemGroupModal } from './useItemGroupModal';
import { FormikErrors } from 'formik';

type Props = { onHide: () => void; data: any; onSave: (result: any) => void };

export const AddItemGroup: React.FC<Props> = ({ onHide, data, onSave }) => {
    const { user } = useAppContext();
    const { isSaving, validation } = useItemGroupModal(onSave, data);

    const renderError = (error: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined) => {
        if (typeof error === 'string') {
            return error;
        }
        return '';
    };

    return (
        <React.Fragment>
            <Modal.Header
                className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
                closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
            >
                <Modal.Title className="text-16">
                    {"Item Group"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
                <form
                    action="#!"
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                    }}
                >
                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                        <div className="lg:col-span-2 xl:col-span-12">
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="group_name"
                                    className="text-base font-medium"
                                >
                                    Group Name
                                </label>
                                {validation.touched.group_name && validation.errors.group_name ? (
                                    <span className="text-sm text-red-500">
                                        {renderError(validation.errors.group_name)}
                                    </span>
                                ) : null}
                            </div>
                            <input
                                type="text"
                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                id="group_name"
                                name="group_name"
                                placeholder="Enter group name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.group_name || ""}
                            />
                        </div>

                        <div className="lg:col-span-2 xl:col-span-12">
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="description"
                                    className="text-base font-medium"
                                >
                                    Description
                                </label>
                                {validation.touched.description && validation.errors.description ? (
                                    <span className="text-sm text-red-500">
                                        {renderError(validation.errors.description)}
                                    </span>
                                ) : null}
                            </div>
                            <textarea
                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                id="description"
                                name="description"
                                placeholder="Enter description"
                                rows={3}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.description || ""}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-50 focus:text-red-500 focus:bg-red-50 active:text-red-500 active:bg-red-50 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                            onClick={onHide}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="inline-block w-4 h-4 mr-1 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </React.Fragment>
    );
};